import React, { useState, useEffect } from 'react';
import { generateTOTPCode, getSecondsUntilNextCode } from '../utils/auth.js';

export default function AuthenticatorPage() {
  const [codes, setCodes] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [copiedId, setCopiedId] = useState(null);

  // Define authenticator cards for each family member
  const authenticators = [
    { id: 'rujaiananth', name: 'Rujai Ananth', seed: 'jk-rujaiananth-2024' },
    { id: 'prajitkannan', name: 'Prajit Kannan', seed: 'jk-prajitkannan-2024' },
    { id: 'thamojkarthic', name: 'Thamoj Karthic', seed: 'jk-thamojkarthic-2024' },
    { id: 'jeyakumar', name: 'Jeyakumar', seed: 'jk-jeyakumar-2024' },
    { id: 'viji', name: 'Viji', seed: 'jk-viji-2024' }
  ];

  const updateCodes = () => {
    const newCodes = authenticators.map(auth => ({
      ...auth,
      code: generateTOTPCode(auth.seed)
    }));
    setCodes(newCodes);
    setSecondsLeft(getSecondsUntilNextCode());
  };

  useEffect(() => {
    updateCodes();

    const interval = setInterval(() => {
      const seconds = getSecondsUntilNextCode();
      setSecondsLeft(seconds);

      // Regenerate codes when hitting new minute
      if (seconds === 60 || seconds === 59) {
        updateCodes();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const progressPercent = (secondsLeft / 60) * 100;

  return (
    <div className="authenticator-page">
      <div className="auth-page-header">
        <h1>🔐 Authenticator</h1>
        <p>Your personal 6-digit security codes - Use these during login</p>
        <div className="auth-timer">
          <div className="timer-circle">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle
                cx="30"
                cy="30"
                r="26"
                fill="none"
                stroke="rgba(212, 175, 55, 0.2)"
                strokeWidth="4"
              />
              <circle
                cx="30"
                cy="30"
                r="26"
                fill="none"
                stroke="#d4af37"
                strokeWidth="4"
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - progressPercent / 100)}`}
                transform="rotate(-90 30 30)"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <div className="timer-text">{secondsLeft}s</div>
          </div>
          <span className="timer-label">Next refresh</span>
        </div>
      </div>

      <div className="auth-cards-grid">
        {codes.map(auth => (
          <div key={auth.id} className="auth-code-card">
            <div className="auth-card-name">{auth.name}</div>
            <div className="auth-code-display">{auth.code}</div>
            <button
              className="copy-code-btn"
              onClick={() => copyToClipboard(auth.code, auth.id)}
            >
              {copiedId === auth.id ? '✓ Copied!' : '📋 Copy Code'}
            </button>
            <div className="auth-card-seed">
              <small>Seed: {auth.seed.substring(0, 16)}...</small>
            </div>
          </div>
        ))}
      </div>

      <div className="auth-info-box">
        <h3>🛡️ How to use</h3>
        <ul>
          <li><strong>During Login:</strong> After entering your password and completing face authentication, you'll be asked to enter a 6-digit code</li>
          <li>Use the code shown here that matches your username</li>
          <li>These codes change every 60 seconds for enhanced security</li>
          <li>Each property/vault has its own unique code</li>
          <li>Codes are generated locally using time-based algorithm</li>
        </ul>
      </div>

      <div className="auth-daily-seed">
        <p>
          📅 Daily Seed: {new Date().toISOString().split('T')[0]}
        </p>
        <small>Codes are seeded with today's date for enhanced security</small>
      </div>
    </div>
  );
}
